import React from 'react';
import { Box, Container, Typography, Stack } from '@mui/material';
import {
  SearchOff,
  Error,
  AccessTime,
  AttachMoney,
  PhoneAndroid,
} from '@mui/icons-material';

const colors = {
  cobaltDeep: '#1a2f6c',
  cobaltMain: '#2b4cc0',
  cobaltBright: '#5a7dff',
  cobaltSky: '#7d9fff',
  cobaltIce: '#b3c9ff',
  midnight: '#0f1a34',
  slate: '#293b6d',
  frost: '#f5f7ff',
};

const problems = [
  {
    icon: SearchOff,
    title: '엑셀에 흩어진 데이터',
    description:
      '금형 정보가 여러 파일에 분산되어 담당자가 아니면 현황 파악이 불가능하고, 데이터의 정합성을 신뢰하기 어렵습니다.',
  },
  {
    icon: Error,
    title: '예기치 못한 설비 고장',
    description:
      '금형 수명이 체계적으로 관리되지 않아, 갑작스러운 고장으로 생산 라인이 멈춰 막대한 손실을 초래합니다.',
  },
  {
    icon: AccessTime,
    title: '금형을 찾는 데 허비되는 시간',
    description:
      '필요한 금형이 어디 있는지 몰라 작업자들이 창고를 헤매며 30분 이상 시간을 낭비하는 일이 비일비재합니다.',
  },
  {
    icon: AttachMoney,
    title: '과도한 도입 비용',
    description:
      '대기업용 ERP 시스템은 수천만 원에 달하는 높은 비용과 복잡한 기능으로 도입을 망설이게 합니다.',
  },
  {
    icon: PhoneAndroid,
    title: '현장과 동떨어진 관리',
    description:
      '사무실 PC에서만 가능한 관리 방식은 실시간 현장 상황을 반영하지 못해 신속한 대응을 어렵게 합니다.',
  },
];

const ProblemSection: React.FC = () => {
  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: `linear-gradient(180deg, ${colors.midnight} 0%, ${colors.cobaltDeep}15 50%, ${colors.midnight} 100%)`,
      }}
    >
      <Container maxWidth="lg">
        {/* 섹션 헤더 */}
        <Stack spacing={2} alignItems="center" textAlign="center" mb={8}>
          <Typography
            variant="overline"
            sx={{
              color: colors.cobaltBright,
              fontSize: '0.9rem',
              letterSpacing: '0.15em',
              fontWeight: 600,
            }}
          >
            문제 제기
          </Typography>
          <Typography
            variant="h2"
            sx={{
              color: colors.frost,
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '3rem' },
            }}
          >
            귀사의 현장은 어떻습니까?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: colors.cobaltIce,
              maxWidth: '700px',
              fontSize: { xs: '1rem', md: '1.1rem' },
              lineHeight: 1.7,
            }}
          >
            귀사의 치열한 현장에서 보이지 않는 비용과 시간이 매일 낭비되고 있을지
            모릅니다. 혹시 다음과 같은 어려움을 겪고 계시지 않으신가요?
          </Typography>
        </Stack>

        {/* 문제 카드 그리드 */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {problems.map((problem, index) => {
            const Icon = problem.icon;
            return (
              <Box key={index} sx={{ flex: '0 0 auto', width: { xs: '100%', md: 'calc(50% - 12px)', lg: 'calc(33.33% - 16px)' } }}>
                <Box
                  sx={{
                    height: '100%',
                    p: 4,
                    borderRadius: '24px',
                    background: `linear-gradient(165deg, ${colors.cobaltDeep}25, ${colors.slate}15)`,
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${colors.cobaltSky}30`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 20px 40px ${colors.cobaltMain}40`,
                      borderColor: colors.cobaltBright,
                    },
                  }}
                >
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(135deg, ${colors.cobaltBright}30, ${colors.cobaltSky}20)`,
                        border: `1px solid ${colors.cobaltBright}40`,
                      }}
                    >
                      <Icon sx={{ fontSize: 32, color: colors.cobaltBright }} />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        color: colors.frost,
                        fontWeight: 600,
                      }}
                    >
                      {problem.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: colors.cobaltIce,
                        lineHeight: 1.7,
                      }}
                    >
                      {problem.description}
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
};

export default ProblemSection;
